import React from 'react';
import classNames from 'classnames';
import propTypes from 'prop-types';

/**
 * This is a shared component between RadioButton and SnookerCheckbox. Its main
 * purpose is to share markup and logic and reduce duplication between the two.
 *
 * This component is a stateless component. All logic around handling whether
 * these elements are checked or disabled should be handled by a parent.
 */
const ToggleInput = ({
  badge,
  checked,
  disabled,
  error,
  id,
  inputClass,
  labelClass,
  onChange,
  name,
  simple,
  subText,
  text,
  type,
  value,
  wrapperClass,
}) => {
  const inputClasses = classNames(inputClass, 'toggle-input__input');
  const labelClasses = classNames(labelClass, 'toggle-input__label', {
    'toggle-input__label--error': !!error,
    'toggle-input__label--simple': simple,
    'toggle-input__label--with-subtext': simple && !!subText,
  });
  const labelTextClasses = classNames('toggle-input__label__text', {
    'text--body--small': !simple,
    'text--button': simple,
  });
  const labelSubTextClasses = classNames('toggle-input__label__sub-text', {
    'subheading--mini': !simple,
    'subheading--small toggle-input__label__sub-text--simple': simple,
  });
  const wrapperClasses = classNames(wrapperClass, 'toggle-input');
  const htmlId = id || `${name}-${value}`;

  const handleChange = event => {
    onChange && onChange(event);
  };

  return (
    <div className={wrapperClasses}>
      <input
        checked={checked}
        className={inputClasses}
        disabled={disabled}
        id={htmlId}
        name={name}
        onChange={handleChange}
        type={type}
        value={value}
      />
      <label className={labelClasses} htmlFor={htmlId}>
        {!simple && (
          <span
            className={`toggle-input__label__icon toggle-input__label__icon--${type}`}
          />
        )}
        <span className={labelTextClasses}>
          {text}
          {subText && <span className={labelSubTextClasses}>{subText}</span>}
        </span>
        {badge && !simple && (
          <span className={'toggle-input__label__badge'}>{badge}</span>
        )}
      </label>

      {!!error && <span className="toggle-input__error">{error}</span>}
    </div>
  );
};

export const toggleInputPropTypes = {
  badge: propTypes.element,
  checked: propTypes.bool.isRequired,
  disabled: propTypes.bool,
  error: propTypes.string,
  id: propTypes.string,
  inputClass: propTypes.string,
  labelClass: propTypes.string,
  name: propTypes.string.isRequired,
  onChange: propTypes.func.isRequired,
  simple: propTypes.bool,
  subText: propTypes.oneOfType([propTypes.string, propTypes.element]),
  text: propTypes.oneOfType([propTypes.string, propTypes.element]).isRequired,
  type: propTypes.string.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool,
    propTypes.number,
  ]),
  wrapperClass: propTypes.string,
};

ToggleInput.propTypes = {
  ...toggleInputPropTypes,
};

export default ToggleInput;
